// src/components/CookieConsent/CookieConsent.jsx
import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import css from "./CookieConsent.module.css";

export default function CookieConsent() {
  const { t } = useTranslation("footer");
  const [show, setShow] = useState(false);
  const [modal, setModal] = useState(false);

  // estados para preferências (não usamos DOM direto)
  const [analyticsOpt, setAnalyticsOpt] = useState(false);

  // refs para foco acessível
  const acceptBtnRef = useRef(null);
  const modalFirstRef = useRef(null);
  const modalCloseRef = useRef(null);

  // Checa se você já tem uma escolha salva
  useEffect(() => {
    try {
      const consent = localStorage.getItem("cookie-consent");
      if (!consent) {
        setShow(true);
      }
    } catch (e) {
      setShow(true);
    }
  }, []);

  // bloqueia scroll quando modal / banner ativo
  useEffect(() => {
    if (modal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [modal]);

  // fechar com ESC quando modal aberto
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        if (modal) setModal(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modal]);

  // foco quando abre modal
  useEffect(() => {
    if (modal) {
      setTimeout(() => modalFirstRef.current?.focus(), 80);
    } else {
      // quando fecha o modal, devolve foco ao botão aceitar (se existir)
      setTimeout(() => acceptBtnRef.current?.focus(), 40);
    }
  }, [modal]);

  function acceptAll() {
    localStorage.setItem("cookie-consent", JSON.stringify({ analytics: true }));
    setShow(false);
    enableAnalytics();
  }

  function rejectAll() {
    localStorage.setItem(
      "cookie-consent",
      JSON.stringify({ analytics: false })
    );
    setShow(false);
  }

  function savePreferences(pref) {
    localStorage.setItem("cookie-consent", JSON.stringify(pref));
    if (pref.analytics) enableAnalytics();
    setModal(false);
    setShow(false);
  }

  function enableAnalytics() {
    if (window.gaInitialized) return;
    window.gaInitialized = true;

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-0W88F84ZG6";
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];

    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    gtag("js", new Date());
    gtag("config", "G-0W88F84ZG6", {
      anonymize_ip: true,
      send_page_view: false,
    });
  }

  if (!show) return null;

  return (
    <>
      {/* BANNER */}
      <div
        className={css.banner}
        role="region"
        aria-label="Cookie consent"
        aria-live="polite"
      >
        <div className={css.text}>
          {t("cookies.consent")}{" "}
          <a href="/privacy" target="_blank" rel="noreferrer">
            {t("policy")}
          </a>
          .
        </div>

        <div className={css.buttons}>
          <button
            className={css.reject}
            onClick={rejectAll}
            aria-label="Recusar cookies"
          >
            {t("cookies.refuse")}
          </button>

          <button
            className={css.settings}
            onClick={() => {
              // preparar estado atual (se já tiver algo salvo)
              try {
                const saved = JSON.parse(
                  localStorage.getItem("cookie-consent") || "{}"
                );
                setAnalyticsOpt(Boolean(saved.analytics));
              } catch (e) {
                setAnalyticsOpt(false);
              }
              setModal(true);
            }}
            aria-haspopup="dialog"
            aria-controls="cookie-modal"
            aria-expanded={modal}
          >
            {t("cookies.config")}
          </button>

          <button
            ref={acceptBtnRef}
            className={css.accept}
            onClick={acceptAll}
            aria-label="Aceitar cookies"
          >
            {t("cookies.accept")}
          </button>
        </div>
      </div>

      {/* Modal de configurações */}
      {modal && (
        <div
          id="cookie-modal"
          className={css.modalOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModal(false);
          }}
        >
          <div className={css.modal} role="document">
            <h3 id="cookie-title">{t("cookies.preferences")}</h3>

            <div className={css.cookieGroup}>
              <h4>{t("cookies.preferencesTitle1")}</h4>
              <p>{t("cookies.preferencesText1")}</p>
              <label className={css.switch} aria-hidden>
                <input type="checkbox" checked disabled />
                <span className={css.slider}></span>
              </label>
            </div>

            <div className={css.cookieGroup}>
              <h4>{t("cookies.preferencesTitle2")}</h4>
              <p>{t("cookies.preferencesText2")}</p>

              <div className={css.row}>
                <label className={css.switch}>
                  <input
                    ref={modalFirstRef}
                    type="checkbox"
                    checked={analyticsOpt}
                    onChange={(e) => setAnalyticsOpt(e.target.checked)}
                    id="analytics-opt"
                    aria-label="Ativar analytics"
                  />
                  <span className={css.slider}></span>
                </label>

                <div style={{ marginLeft: 12 }}>
                  <div className={css.smallLabel}>
                    {t("cookies.preferencesTitle2")}
                  </div>
                  <div className={css.smallDesc}>
                    {t("cookies.preferencesText3")}
                  </div>
                </div>
              </div>
            </div>

            <div className={css.modalButtons}>
              <button
                onClick={() => setModal(false)}
                className={css.cancel}
                ref={modalCloseRef}
              >
                {t("cookies.cancel")}
              </button>

              <button
                onClick={() =>
                  savePreferences({
                    analytics: analyticsOpt,
                  })
                }
                className={css.save}
              >
                {t("cookies.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
